import api from "@/shared/services/apiClient";
import type { TranslationData } from "@/shared/types/common";

export const translateService = {
  translate: async (data: TranslationData) => {
    const payload = {
      text: data.text,
      targetLang: data.targetLang || "ja",
    };
    const response = await api.post("/translate", payload);
    return response.data;
  },

  getLanguages: async () => {
    const response = await api.get("/translate/languages");
    return response.data;
  },
};

export const SUPPORTED_LANGUAGES = [
  { code: "en", name: "English" },
  { code: "ja", name: "Japanese" },
  { code: "vi", name: "Vietnamese" },
  { code: "id", name: "Indonesian" },
  { code: "tl", name: "Tagalog" },
  { code: "bn", name: "Bengali" },
  { code: "km", name: "Khmer" },
  { code: "my", name: "Burmese" },
  { code: "ne", name: "Nepali" },
  { code: "th", name: "Thai" },
] as const;

export type LanguageCode = (typeof SUPPORTED_LANGUAGES)[number]["code"];

/** Unicode flag emoji for each supported language — shared across components. */
export const LANGUAGE_FLAGS: Record<string, string> = {
  en: "🇺🇸",
  ja: "🇯🇵",
  id: "🇮🇩",
  vi: "🇻🇳",
  tl: "🇵🇭",
  bn: "🇧🇩",
  km: "🇰🇭",
  my: "🇲🇲",
  ne: "🇳🇵",
  th: "🇹🇭",
};

/** Native-script display name for each supported language — shared across components. */
export const LANGUAGE_NATIVE_NAMES: Record<string, string> = {
  en: "English",
  ja: "日本語",
  id: "Bahasa Indonesia",
  vi: "Tiếng Việt",
  tl: "Tagalog",
  bn: "বাংলা",
  km: "ភាសាខ្មែរ",
  my: "မြန်မာ",
  ne: "नेपाली",
  th: "ภาษาไทย",
};

export const translateText = async (
  text: string,
  targetLanguage: LanguageCode,
  _sourceLanguage?: LanguageCode,
) => {
  if (!text?.trim()) return { translatedText: text, success: true };

  try {
    const response = await api.post("/translate", {
      text,
      targetLang: targetLanguage,
    });
    const data = response.data?.data;
    return {
      translatedText: data?.translated || text,
      detectedSourceLanguage: data?.detectedSource || undefined,
      success: true,
    };
  } catch (error: any) {
    return { translatedText: text, success: false, error: error.message || "Translation failed" };
  }
};
